import { useEffect, useRef, useState } from "react";
import { ReactSortable } from "react-sortablejs";
import ActivityCard from "./ActivityCard";

// react-sortablejs mutates list items with transient drag flags (`chosen`,
// `selected`) and calls setList on mount and on every choose/unchoose event.
// Only these four fields may ever be persisted to the calendar document.
const sanitizeActivity = (item) => {
    const clean = { id: item.id };
    if (item.title !== undefined) clean.title = item.title;
    if (item.description !== undefined) clean.description = item.description;
    if (item.color !== undefined) clean.color = item.color;
    return clean;
};

const contentKey = (list) => JSON.stringify((list || []).map(sanitizeActivity));

export default function DayConfig( { day, calendar, allActivities, updateCalendar } ){
    const [theseActivities, setTheseActivities] = useState(day.activities ? day.activities : [])

    // Tracks the content last persisted (or received from Firestore) so that
    // sortable's mount/flag churn and our own snapshot echoes never trigger writes.
    const lastSavedRef = useRef(contentKey(day.activities))

    // Sync local state when the day changes remotely (another admin's edit or a
    // snapshot echo). Writes only ever happen from handleSetList below, so this
    // cannot loop.
    useEffect(() => {
        const incoming = day.activities ? day.activities : []
        const incomingKey = contentKey(incoming)
        if (incomingKey !== lastSavedRef.current) {
            lastSavedRef.current = incomingKey
            setTheseActivities(incoming)
        }
    }, [day])

    const handleSetList = (newList) => {
        // Bail out (return prev) when items are identical refs — sortable's
        // mount/choose events mutate flags in place and pass a new array wrapper.
        setTheseActivities((prev) => {
            const sameItems = newList.length === prev.length && newList.every((item, i) => item === prev[i])
            return sameItems ? prev : newList
        })
        const key = contentKey(newList)
        if (key === lastSavedRef.current) return
        lastSavedRef.current = key
        updateCalendar(calendar.id, {
            [`${day.title}`]: newList.map(sanitizeActivity)
        })
    }

    const handleDeleteActivity = (thisID) => {
        let activityIndex = theseActivities.findIndex(activity => activity.id === thisID)
        if(activityIndex > -1){
            let newActivities = [...theseActivities]
            newActivities.splice(activityIndex, 1)
            handleSetList(newActivities)
        }
    }

    return(
        <ReactSortable
            key={`react-sortable-${calendar.id}-${day.title}`}
            clone={item => sanitizeActivity(item)}
            list={theseActivities}
            setList={handleSetList}
            group={{ name: "activities", pull: "clone" }}
            animation={200}
            easing="ease-out"
            >
            {theseActivities?.map((activity, i) => (
                <ActivityCard
                    handleDeleteActivity={handleDeleteActivity}
                    key={`activity-card-${calendar.id}-${day.title}-${activity.id}-${i}`}
                    activity={activity}
                    i={i}
                    maxWidth='100%'
                    allActivities={allActivities}
                    calendar={calendar}
                    day={day}
                    />
            ))}
        </ReactSortable>
    )
}
